import { PlaybookStatus, User } from '@driftt/api-types'
import {
  Box,
  Button,
  Flex,
  Header,
  IconMenuButton,
  InputText,
  Link,
  Select,
  Table,
} from '@driftt/dds'
import { Subnav } from '@driftt/tide-subnav'
import { showTransientNotification } from '@driftt/traffic-control'
import { pluralize } from 'humanize-plus'
import { indexBy, pathOr, prop } from 'ramda'
import React, { useMemo } from 'react'
import { useState } from 'react'
import { useMutation } from 'react-query'
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux'

import {
  archiveBlock,
  cloneBlock,
  createPlaybookFormattedBlock,
  deleteBlock,
  unarchiveBlock,
} from 'api/blocks'
import { BlockTableRow } from 'api/blocks'
import { PlaybookUpdatesRequiredInCell } from 'components/BlocksTable/helpers'
import { PlaybookUpdatedAuthorCell } from 'components/PlaybooksTable/components/TableRow'
import { APP_PAGE_TITLES } from 'constants/pageTitles'
import { useAllOrgBlocks } from 'hooks/useBlocksList'
import useDebounce from 'hooks/useDebounce'
import useDocumentTitle from 'hooks/usePageTitle'
import { userHasAnyPlaybookAccess } from 'utils/access'
import { getSubNavNodes } from 'utils/getSubNavNodes'
import { isEmptyOrNil } from 'utils/index'
import { formatTime } from 'utils/moment'
interface BlockStatusOption {
  label: string
  value: PlaybookStatus.ENABLED | PlaybookStatus.ARCHIVED
}

const BLOCK_STATUS_OPTIONS: BlockStatusOption[] = [
  {
    label: 'Active reusable blocks',
    value: PlaybookStatus.ENABLED,
  },
  {
    label: 'Archived reusable blocks',
    value: PlaybookStatus.ARCHIVED,
  },
]
type OnChangeType = (value: PlaybookStatus) => void

const BlocksTable = () => {
  const [nameSearch, setNameSearch] = useState('')
  const [blockStatusOption, setBlockStatusOption] = useState(
    BLOCK_STATUS_OPTIONS[0]
  )

  const [sidebarIsOpen, setSidebarIsOpen] = useState(false)

  const debouncedNameSearch = useDebounce(nameSearch)
  const hasAnyPlaybookAccess = userHasAnyPlaybookAccess()
  const userId = useSelector(({ user }: RootStateOrAny) => user.id)
  const orgId = useSelector(({ org }: RootStateOrAny) => org.id)
  const orgUsers = useSelector(pathOr<User[]>([], ['org', 'users']))
  const orgUsersById = useMemo(() => indexBy(prop('id'), orgUsers), [orgUsers])
  const {
    isLoading,
    blocks: blocksTableData,
    handleTableStateChange,
    refetchTableState,
    pagination,
    rowTotal,
  } = useAllOrgBlocks({
    status: blockStatusOption.value,
    nameQuery: debouncedNameSearch,
  })

  useDocumentTitle(APP_PAGE_TITLES.BLOCK)

  const SUB_NAV_NODES = getSubNavNodes()

  const dispatch = useDispatch()

  const createBlock = () => {
    createPlaybookFormattedBlock(userId, orgId)
      .then(({ data }) => {
        window.location.pathname = `/playbooks/blocks/editor/${data.meta.blockId}/visual-builder`
      })
      .catch(() => {
        dispatch(
          showTransientNotification({
            type: 'error',
            message: `Could not create reusable block`,
          })
        )
      })
  }

  const handleOnStatusChange = (value: BlockStatusOption) => {
    setBlockStatusOption(value)
  }

  const doClone = useMutation(cloneBlock, {
    onSuccess: ({ data }) => {
      refetchTableState()
      dispatch(
        showTransientNotification({
          type: 'success',
          message: `Successfully created ${data.interaction.name}`,
        })
      )
    },
    onError: () => {
      dispatch(
        showTransientNotification({
          type: 'error',
          message: `Could not clone reusable block`,
        })
      )
    },
  })

  const doArchive = useMutation(archiveBlock, {
    onSuccess: () => {
      refetchTableState()
      dispatch(
        showTransientNotification({
          type: 'success',
          message: `Successfully archived block`,
        })
      )
    },
    onError: () => {
      dispatch(
        showTransientNotification({
          type: 'error',
          message: `Could not archive block`,
        })
      )
    },
  })

  const doDelete = useMutation(deleteBlock, {
    onSuccess: () => {
      refetchTableState()
      dispatch(
        showTransientNotification({
          type: 'success',
          message: `Successfully deleted block`,
        })
      )
    },
    onError: () => {
      dispatch(
        showTransientNotification({
          type: 'error',
          message: `Could not delete block`,
        })
      )
    },
  })

  const doUnarchive = useMutation(unarchiveBlock, {
    onSuccess: () => {
      refetchTableState()
      dispatch(
        showTransientNotification({
          type: 'success',
          message: `Successfully restored block`,
        })
      )
    },
    onError: () => {
      dispatch(
        showTransientNotification({
          type: 'error',
          message: `Could not restore block`,
        })
      )
    },
  })

  const BlocksActionCell = ({
    blockRowData,
  }: {
    blockRowData: BlockTableRow
  }) => {
    const { blockId, playbookBlockData } = blockRowData

    type ManagedPlaybookStatus =
      | PlaybookStatus.ARCHIVED
      | PlaybookStatus.ENABLED

    const MENU_ACTIONS_BY_STATUS = Object.freeze({
      [PlaybookStatus.ARCHIVED]: [
        {
          name: 'Restore',
          action: () => doUnarchive.mutate(blockId),
        },
        {
          name: 'Delete',
          action: () => doDelete.mutate(blockId),
          disabled: !isEmptyOrNil(playbookBlockData),
        },
      ],
      [PlaybookStatus.ENABLED]: [
        {
          name: 'Clone',
          action: () => doClone.mutate(blockId),
        },
        {
          name: 'Archive',
          action: () => doArchive.mutate(blockId),
          disabled: !isEmptyOrNil(playbookBlockData),
        },
      ],
    })

    return (
      <IconMenuButton
        iconVariant="spillover"
        title=""
        options={
          MENU_ACTIONS_BY_STATUS[
            blockStatusOption.value as ManagedPlaybookStatus
          ]
        }
      />
    )
  }

  return (
    <>
      {hasAnyPlaybookAccess && (
        <Subnav
          title="Playbooks"
          nodes={SUB_NAV_NODES}
          open={sidebarIsOpen}
          onToggle={() => setSidebarIsOpen(!sidebarIsOpen)}
        />
      )}
      <Flex direction="column" gap={0} fullWidth fullHeight>
        <Box fullWidth css={{ padding: '$4 $6' }}>
          <Flex fullWidth justifyContent="space-between">
            <Header>Reusable blocks</Header>
            <Box>
              <Button onClick={createBlock}>Create reusable block</Button>
            </Box>
          </Flex>
        </Box>
        <Flex
          grow={1}
          fullHeight
          fullWidth
          css={{ background: '$gray2', padding: '$3 $6', overflow: 'auto' }}
          direction="column"
          gap={0}
        >
          <Flex
            fullWidth
            direction="row"
            justifyContent="space-between"
            css={{ padding: '$2 $3', background: '$white' }}
          >
            <InputText
              label=""
              onChange={({ target }) => setNameSearch(target.value)}
              value={nameSearch}
              placeholder="Search for block..."
            />
            <Box>
              <Select
                options={BLOCK_STATUS_OPTIONS}
                label=""
                value={blockStatusOption}
                onChange={(newValue): void => {
                  handleOnStatusChange(newValue as BlockStatusOption)
                }}
              />
            </Box>
          </Flex>
          <Flex grow={1} fullWidth>
            <Table
              isLoading={isLoading}
              rows={blocksTableData}
              height="100%"
              disableBoxShadow={true}
              columns={[
                {
                  value: 'blockName',
                  header: 'Reusable block',
                  size: 194,
                  cell: (row) => (
                    <Link
                      external
                      href={`/playbooks/blocks/editor/${row.blockId}/visual-builder`}
                      variant="object"
                    >
                      {row.blockName}
                    </Link>
                  ),
                },
                {
                  value: 'playbookBlockData',
                  header: 'Used in',
                  size: 194,
                  cell: (row) =>
                    `${row.playbookBlockData?.length} ${pluralize(
                      row.playbookBlockData?.length,
                      blockStatusOption.value === PlaybookStatus.ENABLED
                        ? 'playbook'
                        : 'archived playbook'
                    )}`,
                },
                {
                  value: 'updatesRequired',
                  header: 'Updates required in',
                  size: 194,
                  cell: (row) => (
                    <PlaybookUpdatesRequiredInCell
                      playbookBlockData={row.playbookBlockData}
                    />
                  ),
                },
                {
                  value: 'updatedAt',
                  header: 'Last updated',
                  size: 194,
                  cell: (row) => formatTime(row.updatedAt, 'MM/DD/YY'),
                },
                {
                  value: 'updatedBy',
                  header: 'Updated by',
                  size: 80,
                  cell: (row) => (
                    <PlaybookUpdatedAuthorCell
                      updatedAuthorId={row.updatedBy}
                      componentOrgUsers={orgUsersById}
                    />
                  ),
                },
                {
                  value: 'actions',
                  header: '',
                  size: 20,
                  cell: (row) => (
                    <>
                      <BlocksActionCell blockRowData={row} />
                    </>
                  ),
                },
              ]}
              pagination={{
                defaultPagination: pagination,
                rowTotal,
              }}
              emptyState={{
                minHeight: 600,
                title:
                  'Update content used across playbooks from a single place',
              }}
              onStateChange={handleTableStateChange}
            />
          </Flex>
        </Flex>
      </Flex>
    </>
  )
}

export default BlocksTable
